import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { resizeImageForKind } from '@shared/partner-ui/utils/image-processing';
import {
  AccountDto,
  AccountUpdatePayload,
  AccountMediaKind
} from '@shared/partner-ui/models/winery-account';

export interface LoginPayload {
  personalEmail: string;
  password: string;
}

export interface RegisterPayload {
  companyName: string;
  contactName: string;
  personalEmail: string;
  publicEmail: string;
  password: string;
  language: string;
}

export interface ForgotPasswordPayload {
  personalEmail: string;
}

export interface ResetPasswordPayload {
  token: string;
  password: string;
}

export interface VerifyEmailPayload {
  code: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiBaseUrl;
  private readonly currentUser = signal<AccountDto | null>(null);
  private readonly hydrated = signal(false);
  private readyResolver: (() => void) | null = null;
  private readonly readyPromise = new Promise<void>((resolve) => {
    this.readyResolver = resolve;
  });

  readonly user = computed(() => this.currentUser());
  readonly isAuthenticated = computed(() => !!this.currentUser());
  readonly isHydrated = computed(() => this.hydrated());
  readonly impersonation = computed(() => this.currentUser()?.impersonation ?? null);
  readonly isImpersonating = computed(() => this.currentUser()?.impersonation?.isActive === true);
  readonly isReadOnlyPreview = computed(() => this.currentUser()?.impersonation?.readOnly === true);

  constructor() {
    void this.bootstrap();
  }

  private async bootstrap(): Promise<void> {
    try {
      await this.refreshAccount().catch(() => undefined);
    } finally {
      this.hydrated.set(true);
      this.readyResolver?.();
    }
  }

  async ensureInitialized(): Promise<void> {
    return this.readyPromise;
  }

  async login(payload: LoginPayload): Promise<AccountDto> {
    const account = await firstValueFrom(
      this.http.post<AccountDto>(`${this.baseUrl}/auth/login`, payload, { withCredentials: true })
    );
    this.currentUser.set(account);
    return account;
  }

  async register(payload: RegisterPayload): Promise<AccountDto> {
    const account = await firstValueFrom(
      this.http.post<AccountDto>(`${this.baseUrl}/auth/register`, payload, { withCredentials: true })
    );
    this.currentUser.set(account);
    return account;
  }

  async requestPasswordReset(payload: ForgotPasswordPayload): Promise<void> {
    await firstValueFrom(
      this.http.post<void>(`${this.baseUrl}/auth/password/forgot`, payload, {
        withCredentials: true
      })
    );
  }

  async resetPassword(payload: ResetPasswordPayload): Promise<AccountDto> {
    const account = await firstValueFrom(
      this.http.post<AccountDto>(`${this.baseUrl}/auth/password/reset`, payload, {
        withCredentials: true
      })
    );
    this.currentUser.set(account);
    return account;
  }

  async logout(): Promise<void> {
    if (this.isImpersonating()) {
      await this.stopImpersonation();
      return;
    }

    await firstValueFrom(
      this.http.post<void>(`${this.baseUrl}/auth/logout`, {}, { withCredentials: true })
    );
    this.currentUser.set(null);
  }

  async stopImpersonation(): Promise<AccountDto | null> {
    await firstValueFrom(
      this.http.post<void>(`${this.baseUrl}/auth/impersonation/logout`, {}, { withCredentials: true })
    );
    return this.refreshAccount();
  }

  async refreshAccount(): Promise<AccountDto | null> {
    try {
      const account = await firstValueFrom(
        this.http.get<AccountDto>(`${this.baseUrl}/account`, { withCredentials: true })
      );
      this.currentUser.set(account);
      return account;
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        this.currentUser.set(null);
        return null;
      }
      throw error;
    }
  }

  async updateAccount(payload: AccountUpdatePayload): Promise<AccountDto> {
    const account = await firstValueFrom(
      this.http.put<AccountDto>(`${this.baseUrl}/account`, payload, { withCredentials: true })
    );
    this.currentUser.set(account);
    return account;
  }

  async updateShowWhenVerified(showWhenVerified: boolean): Promise<AccountDto> {
    const account = await firstValueFrom(
      this.http.put<AccountDto>(
        `${this.baseUrl}/account/visibility`,
        { showWhenVerified },
        { withCredentials: true }
      )
    );
    this.currentUser.set(account);
    return account;
  }

  async updateLanguage(language: string): Promise<AccountDto> {
    const account = await firstValueFrom(
      this.http.put<AccountDto>(`${this.baseUrl}/account/language`, { language }, { withCredentials: true })
    );
    this.currentUser.set(account);
    return account;
  }

  async sendEmailVerificationCode(): Promise<void> {
    await firstValueFrom(
      this.http.post<void>(`${this.baseUrl}/auth/email/verification/send`, {}, { withCredentials: true })
    );
  }

  async verifyEmailCode(payload: VerifyEmailPayload): Promise<AccountDto> {
    const account = await firstValueFrom(
      this.http.post<AccountDto>(`${this.baseUrl}/auth/email/verification/confirm`, payload, {
        withCredentials: true
      })
    );
    this.currentUser.set(account);
    return account;
  }

  async uploadAccountMedia(kind: AccountMediaKind, file: File): Promise<AccountDto> {
    const processed = await resizeImageForKind(kind, file);

    const presign = await firstValueFrom(
      this.http.post<MediaUploadUrlResponse>(
        `${this.baseUrl}/account/media/upload-url`,
        {
          kind,
          fileName: processed.fileName,
          contentType: processed.contentType
        },
        { withCredentials: true }
      )
    );

    const uploadResponse = await fetch(presign.uploadUrl, {
      method: 'PUT',
      body: processed.blob,
      headers: {
        'Content-Type': processed.contentType
      }
    });

    if (!uploadResponse.ok) {
      throw new Error('Unable to upload image.');
    }

    const account = await firstValueFrom(
      this.http.put<AccountDto>(
        `${this.baseUrl}/account/media`,
        { kind, key: presign.key },
        { withCredentials: true }
      )
    );

    this.currentUser.set(account);
    return account;
  }

  async removeAccountMedia(kind: AccountMediaKind): Promise<AccountDto> {
    const account = await firstValueFrom(
      this.http.request<AccountDto>('DELETE', `${this.baseUrl}/account/media`, {
        body: { kind },
        withCredentials: true
      })
    );
    this.currentUser.set(account);
    return account;
  }
}

interface MediaUploadUrlResponse {
  uploadUrl: string;
  key: string;
  publicUrl: string;
}
